// @need-translate
import {useRouter} from 'next/router';
import {ReactNode, useEffect} from 'react';
import {t} from '../../../common/getTranslations';
import {requestContext} from '../../../common/requestContext';
import useTypedDispatch from '../../../common/useTypedDispatch';
import {Loading} from '../../../design-system/atoms/Loading/Loading';
import {NoticeQueryType} from '../../../design-system/pages/Notice/NoticeQueryType.enum';
import {getSerializedPagewebError} from '../../../errors/getSerializedPagewebError.function';
import {PagewebError} from '../../../errors/PagewebError.class';
import {AnnouncementTarget} from '../../../graphql/generated/types';
import {useNoticesQuery} from '../../../wrapper/appWrapper';
import {setNotice, setNoticeId, setNoticePage} from '../state/noticeSlice';
import {getNoticeData} from '../util/getNoticeData';
import {getNoticePage} from '../util/getNoticePage';
import {getNoticeUrl} from '../util/getNoticeUrl';

export function NoticeFetcherHeadless({children}: {children: ReactNode}) {
  const router = useRouter();
  const dispatch = useTypedDispatch();
  const {isMobile, isIos} = requestContext.requestInfo.display;
  const {data, error, isFetching} = useNoticesQuery({
    target: isMobile
      ? isIos
        ? AnnouncementTarget.Ios
        : AnnouncementTarget.Android
      : AnnouncementTarget.All,
  });

  const {query} = router;
  const params = query.pathParams as string[] | undefined;
  const noticeUid = params?.[0] || (query.notice_uid as string | undefined);
  const noticePage = query.notice_page as string | undefined;

  useEffect(() => {
    if (data?.notices) {
      // 공지사항이 없으면 dispatch 하지 않음
      if (data?.notices.totalCount === 0) {
        return;
      }

      const noticeData = getNoticeData(data?.notices);
      dispatch(setNotice(noticeData));
      dispatch(setNoticeId(noticeUid));

      const foundNoticePage =
        Number(noticePage) || getNoticePage(noticeData, noticeUid);
      dispatch(setNoticePage(foundNoticePage));

      // 페이지와 ID가 둘 다 들어온 경우 ID 기준으로 redirect
      if (noticePage && noticeUid) {
        router.replace(getNoticeUrl(NoticeQueryType.Id, noticeUid));
      } else {
        // 페이지와 ID가 둘 다 없는 경우 첫 글로 redirect
        const hasNotBothPageAndId = !noticePage && !noticeUid;
        // 1페이지인 경우 첫 글을 항상 열어주도록 구현. 첫 글로 redirect ( 기획 요청 사항 )
        const isFirstPage = noticePage && Number(noticePage) === 1;
        // query로 들어온 페이지가 공지사항 총 페이지 범위를 벗어난 경우 첫 글로 redirect
        const isExceedPageRange =
          foundNoticePage < 1 || foundNoticePage > noticeData.maxPage;

        if (hasNotBothPageAndId || isFirstPage || isExceedPageRange) {
          router.replace(
            getNoticeUrl(NoticeQueryType.Id, noticeData.items[0].id)
          );
        }
      }
    }
  }, [data, dispatch, noticePage, noticeUid, router]);

  if (!children) {
    return null;
  }
  if (error) {
    throw new PagewebError(getSerializedPagewebError(error));
  }

  if (!isFetching && !!data && data.notices?.totalCount !== 0) {
    return <>{children}</>;
  }

  return (
    <div className="grow flex-center-col">
      {isFetching && <Loading />}
      {!isFetching && data && data.notices?.totalCount === 0 && (
        <div className="grow flex-center-col">
          {t('more_notification_empty')}
        </div>
      )}
    </div>
  );
}
